import React, { useEffect, useState } from "react";
import { Calendar, Clock, Trash2, RefreshCw, AlertCircle } from "lucide-react";
import { buildApiUrl } from '../../services/apiHelpers';

const DS = {
  colors: {
    primary: "#0369a1",
    primaryLight: "#e0f2fe",
    success: "#16a34a",
    successLight: "#dcfce7",
    danger: "#dc2626",
    dangerLight: "#fee2e2",
    warning: "#ea580c",
    warningLight: "#ffedd5",
    
    surface: "#ffffff",
    surfaceAlt: "#f9fafb",
    
    text: {
      primary: "#0f172a",
      secondary: "#475569",
      tertiary: "#64748b",
      muted: "#94a3b8",
    },
    
    border: "#e2e8f0",
  },
  
  spacing: { xs: 4, sm: 8, md: 12, lg: 16, xl: 24 },
  radius: { sm: 6, md: 8, lg: 12 },
  shadow: { sm: "0 2px 6px rgba(0,0,0,0.06)", md: "0 4px 12px rgba(0,0,0,0.08)" },
};

export default function FindMyBooking({ bookings = [], onStartReschedule }) {
  const [localBookings, setLocalBookings] = useState(
    Array.isArray(bookings) ? bookings : []
  );
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    setLocalBookings(Array.isArray(bookings) ? bookings : []);
  }, [bookings]);

  const handleCancel = async (id) => {
    if (!window.confirm("Are you sure you want to cancel this booking?")) {
      return;
    }

    setActionLoading(id);
    try {
      // 🔥 FIXED: Changed port from 3000 to 8080 to hit Spring Boot Backend
      const response = await fetch(buildApiUrl(`appointments/cancel/${id}`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        console.error("Backend error:", data);
        throw new Error(data.message || `Server error: ${response.status}`);
      }

      // Only update UI if API call was successful
      if (data.success) {
        setLocalBookings((prev) =>
          prev.map((b) => (b.id === id ? { ...b, status: "CANCELLED" } : b))
        );
        alert("Booking cancelled successfully!");
      } else {
        throw new Error(data.message || "Failed to cancel booking");
      }
    } catch (err) {
      console.error("Cancel error:", err);
      alert(`${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReschedule = (id, doctorId) => {
    if (onStartReschedule) {
      onStartReschedule(id, doctorId);
    } else {
      alert("Reschedule functionality needs to be wired up in the BookAppointment.jsx parent component.");
    }
  };

  if (!localBookings || localBookings.length === 0) {
    return (
      <div
        style={{
          padding: DS.spacing.xl,
          background: DS.colors.surfaceAlt,
          borderRadius: DS.radius.lg,
          textAlign: "center",
          color: DS.colors.text.tertiary,
        }}
      >
        <AlertCircle
          size={32}
          style={{ margin: `0 auto ${DS.spacing.md}px`, opacity: 0.3 }}
        />
        <p style={{ margin: 0, fontWeight: 600 }}>No bookings found</p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: DS.spacing.md,
        animation: "slideUp 0.4s ease",
      }}
    >
      {localBookings.map((booking) => {
          const isBooked = booking.status === "BOOKED";
          const isCancelled = booking.status === "CANCELLED";
          const isCompleted = booking.status === "COMPLETED";

        return (
          <div
            key={booking.id}
            style={{
              padding: DS.spacing.lg,
              border: `1px solid ${
                isCancelled ? DS.colors.border : DS.colors.primary + "30"
              }`,
              background: isCancelled ? DS.colors.surfaceAlt : DS.colors.surface,
              borderRadius: DS.radius.lg,
              transition: "all 0.2s",
              opacity: isCancelled ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isCancelled) {
                e.currentTarget.style.boxShadow = DS.shadow.md;
                e.currentTarget.style.borderColor = DS.colors.primary;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.borderColor =
                isCancelled ? DS.colors.border : DS.colors.primary + "30";
            }}
          >
            {/* Header with token */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: DS.spacing.lg,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: DS.colors.text.tertiary,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    marginBottom: DS.spacing.xs,
                  }}
                >
                  Appointment Details
                </div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: DS.colors.text.primary,
                  }}
                >
                  Token #{booking.tokenNo || "—"}
                </div>
              </div>

              {/* Status Badge */}
              <div
                style={{
                  padding: `${DS.spacing.sm}px ${DS.spacing.lg}px`,
                  borderRadius: DS.radius.lg,
                  background:
                    booking.status === "COMPLETED"
                      ? DS.colors.successLight
                      : booking.status === "CANCELLED"
                      ? DS.colors.dangerLight
                      : booking.status === "CONSULTING"
                      ? DS.colors.primaryLight
                      : DS.colors.warningLight,
                  color:
                    booking.status === "COMPLETED"
                      ? DS.colors.success
                      : booking.status === "CANCELLED"
                      ? DS.colors.danger
                      : booking.status === "CONSULTING"
                      ? DS.colors.primary
                      : DS.colors.warning,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  textAlign: "center",
                  minWidth: "90px",
                }}
              >
                {booking.status || "—"}
              </div>
            </div>

            {/* Date & Time */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: DS.spacing.lg,
                marginBottom: DS.spacing.lg,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: DS.spacing.md,
                  padding: `${DS.spacing.md}px ${DS.spacing.lg}px`,
                  background: DS.colors.surfaceAlt,
                  borderRadius: DS.radius.lg,
                }}
              >
                <Calendar size={16} color={DS.colors.primary} />
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: DS.colors.text.tertiary,
                      fontWeight: 600,
                    }}
                  >
                    Date
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: DS.colors.text.primary,
                    }}
                  >
                    {booking.appointmentDate
                      ? new Date(booking.appointmentDate).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: DS.spacing.md,
                  padding: `${DS.spacing.md}px ${DS.spacing.lg}px`,
                  background: DS.colors.surfaceAlt,
                  borderRadius: DS.radius.lg,
                }}
              >
                <Clock size={16} color={DS.colors.primary} />
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: DS.colors.text.tertiary,
                      fontWeight: 600,
                    }}
                  >
                    Time
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: DS.colors.text.primary,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {booking.slotTime
                      ? (() => { const [hours, minutes] = String(booking.slotTime).split(":").map(Number); const suffix = hours >= 12 ? "PM" : "AM"; return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${suffix}`; })()
                      : "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            {isBooked && !isCancelled && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: DS.spacing.md,
                }}
              >
                <button
                  onClick={() => handleCancel(booking.id)}
                  disabled={actionLoading === booking.id}
                  style={{
                    padding: `${DS.spacing.md}px ${DS.spacing.lg}px`,
                    border: `1px solid ${DS.colors.danger}30`,
                    background: DS.colors.dangerLight,
                    color: DS.colors.danger,
                    borderRadius: DS.radius.lg,
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: actionLoading === booking.id ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: DS.spacing.sm,
                    opacity: actionLoading === booking.id ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (actionLoading !== booking.id) {
                      e.currentTarget.style.background = DS.colors.danger;
                      e.currentTarget.style.color = "#fff";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = DS.colors.dangerLight;
                    e.currentTarget.style.color = DS.colors.danger;
                  }}
                >
                  {actionLoading === booking.id ? (
                    <>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          border: "2px solid currentColor",
                          borderTopColor: "transparent",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      Cancelling...
                    </>
                  ) : (
                    <>
                      <Trash2 size={13} strokeWidth={2} />
                      Cancel
                    </>
                  )}
                </button>

                <button
                  onClick={() =>
                    handleReschedule(booking.id, booking.doctorId)
                  }
                  disabled={actionLoading === booking.id}
                  style={{
                    padding: `${DS.spacing.md}px ${DS.spacing.lg}px`,
                    border: "none",
                    background: DS.colors.primary,
                    color: "#fff",
                    borderRadius: DS.radius.lg,
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: actionLoading === booking.id ? "not-allowed" : "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: DS.spacing.sm,
                    opacity: actionLoading === booking.id ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (actionLoading !== booking.id) {
                      e.currentTarget.style.background = "#0c4a6e";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = DS.colors.primary;
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {actionLoading === booking.id ? (
                    <>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          border: "2px solid currentColor",
                          borderTopColor: "transparent",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      Rescheduling...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={13} strokeWidth={2} />
                      Reschedule
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Status Messages */}
            {isCancelled && (
              <div
                style={{
                  padding: `${DS.spacing.md}px ${DS.spacing.lg}px`,
                  background: DS.colors.dangerLight,
                  border: `1px solid ${DS.colors.danger}30`,
                  borderRadius: DS.radius.lg,
                  color: DS.colors.danger,
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                This appointment has been cancelled
              </div>
            )}

            {isCompleted && (
              <div
                style={{
                  padding: `${DS.spacing.md}px ${DS.spacing.lg}px`,
                  background: DS.colors.successLight,
                  border: `1px solid ${DS.colors.success}30`,
                  borderRadius: DS.radius.lg,
                  color: DS.colors.success,
                  fontSize: 12,
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                Appointment completed
              </div>
            )}
          </div>
        );
      })}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}